import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';
import { OrgFeatures } from '@bb/domain/dist/types';
import type { RootScopeType } from '@app/caferealtor/Default.controller';
import errorReporter from '@app/errorReporter';
import {
  isLimitedAccessOnUnclaimedBrokerage,
  replaceMarkupWithTags,
} from 'utils';
import { Spin, theme } from 'components';
import { getListing } from 'api/ListingController';
import { getOfferAgents, sendOfferMessage } from 'api/OfferController';
import {
  getEmailSignatures,
  getTemplates,
  Template,
} from 'api/TemplateController';
import { Listing } from 'api/ListingController/getListing';
import { RecipientType } from '../OfferMessageSentPage/types';
import OfferMessageEditor from '../OfferMessageEditor';
import OfferMessageSentPage from '../OfferMessageSentPage';
import AgentsList from './AgentsList';
import { EnhancedAppointmentVT, ListingAgentType } from './types';
import {
  checkForDuplicatesLA,
  checkIfRecipientRemoved,
  getListingAgents,
} from './helpers';
import OfferMessageHeader from './OfferMessageHeader';
import OfferMessageTabNav from './OfferMessageTabNav';

export interface OfferMessageProps {
  $scope: any;
  $rootScope: RootScopeType;
  $location: any;
  listingId?: string;
  ErrorService: any;
  ModalService: any;
}

const messageSentToOptions = (hasFeature: (feature: string) => boolean) => [
  {
    label: 'All Agents',
    value: 'all',
  },
  {
    label: 'Completed Showings Only',
    value: 'completedAppointmentsOnly',
  },
  ...(hasFeature('OFFERS')
    ? [
        {
          label: 'Agents with Registered Offers',
          value: 'registered',
        },
      ]
    : []),
  {
    label: 'Upcoming Showings',
    value: 'upcomingAppointments',
  },
  {
    label: 'Virtual Showings',
    value: 'virtualAppointments',
  },
];

function OfferMessage({
  listingId,
  $location,
  $scope,
  $rootScope,
  ErrorService,
  ModalService,
}: OfferMessageProps) {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [agentsRecipient, setAgentsRecipient] = useState<RecipientType[]>([]);
  const [agentsRemoved, setAgentsRemoved] = useState<RecipientType[]>([]);
  const [agentsUnsubscribedState, setAgentsUnsubscribedState] = useState<
    RecipientType[]
  >([]);
  const [listingAgentsRecipient, setListingAgentsRecipient] = useState<
    ListingAgentType[]
  >([]);
  const [listingAgentsRemoved, setListingAgentsRemoved] = useState<
    ListingAgentType[]
  >([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [signature, setSignature] = useState('');
  const [listingLink, setListingLink] = useState(`#/listing/${listingId}/view`);
  const [listing, setListing] = useState<Listing>();
  const [mainTab, setMainTab] = useState('sendNewMessage');
  const [agentsListTab, setAgentsListTab] = useState('recipient');
  const [sentTo, setSentTo] = useState('all');
  const [message, setMessage] = useState<{ subject: string; body: string }>({
    subject: '',
    body: '',
  });

  const hasLimitedAccess = isLimitedAccessOnUnclaimedBrokerage(
    $rootScope.auth,
    $rootScope.orgStatus,
    $rootScope.currentPlan,
  );
  const panelBodyStyles: React.CSSProperties = {
    filter: hasLimitedAccess ? `blur(${theme.pageBlurRadius})` : undefined,
    pointerEvents: hasLimitedAccess ? 'none' : undefined,
  };

  const deleteAgent = useCallback(
    (agentEmail: string, agentId?: string) => {
      let removedAgent: RecipientType;
      const newAgentsRecipientState: RecipientType[] = [];
      agentsRecipient.forEach((agent: RecipientType) => {
        if (
          (agentId && agentId === agent.id) ||
          (!agentId && agent.email === agentEmail)
        ) {
          removedAgent = agent;
        } else {
          newAgentsRecipientState.push(agent);
        }
      });

      setAgentsRecipient(newAgentsRecipientState);
      setAgentsRemoved([...agentsRemoved, removedAgent]);
    },
    [agentsRemoved, agentsRecipient],
  );

  const deleteListingAgent = useCallback(
    (agentEmail: string, agentId?: string) => {
      let removedAgent: ListingAgentType;
      const newListingAgentsRecipientState: ListingAgentType[] = [];
      listingAgentsRecipient.forEach((agent: ListingAgentType) => {
        if (
          (agentId && agentId === agent.id) ||
          (!agentId && agent.email === agentEmail)
        ) {
          removedAgent = agent;
        } else {
          newListingAgentsRecipientState.push(agent);
        }
      });

      setListingAgentsRecipient(newListingAgentsRecipientState);
      setListingAgentsRemoved([...listingAgentsRemoved, removedAgent]);
    },
    [listingAgentsRemoved, listingAgentsRecipient],
  );

  const restoreAgent = useCallback(
    (agentEmail: string, agentId?: string) => {
      let restoredAgent: RecipientType;
      const newAgentsRemovedState: RecipientType[] = [];
      agentsRemoved.forEach((agent: RecipientType) => {
        if (
          (agentId && agentId === agent.id) ||
          (!agentId && agent.email === agentEmail)
        ) {
          restoredAgent = agent;
        } else {
          newAgentsRemovedState.push(agent);
        }
      });

      setAgentsRemoved(newAgentsRemovedState);
      setAgentsRecipient([...agentsRecipient, restoredAgent]);
    },
    [agentsRemoved, agentsRecipient],
  );

  const restoreListingAgent = useCallback(
    (agentEmail: string, agentId?: string) => {
      let restoredAgent: ListingAgentType;
      const newListingAgentsRemovedState: ListingAgentType[] = [];
      listingAgentsRemoved.forEach((agent: ListingAgentType) => {
        if (
          (agentId && agentId === agent.id) ||
          (!agentId && agent.email === agentEmail)
        ) {
          restoredAgent = agent;
        } else {
          newListingAgentsRemovedState.push(agent);
        }
      });

      setListingAgentsRemoved(newListingAgentsRemovedState);
      setListingAgentsRecipient([...listingAgentsRecipient, restoredAgent]);
    },
    [listingAgentsRecipient, listingAgentsRemoved],
  );

  const onSend = async () => {
    setDataLoaded(false);
    setTemplateLoaded(false);
    const data = {
      sentTo,
      agents: {
        recipients: [...agentsRecipient, ...listingAgentsRecipient],
        removed: [...agentsRemoved, ...listingAgentsRemoved],
        unsubscribed: agentsUnsubscribedState,
      },
      listing,
      listingId,
      subject: message.subject,
      body: replaceMarkupWithTags(message.body),
    };

    await sendOfferMessage(data);
    window.location.replace(`#/listing/${listingId}/view`);
  };

  const setSubject = useCallback((value: string) => {
    setMessage(curState => ({ ...curState, subject: value }));
  }, []);

  const setBody = useCallback((value: string) => {
    setMessage(curState => ({ ...curState, body: replaceMarkupWithTags(value) }));
  }, []);

  useEffect(() => {
    const fetchListing = async () => {
      if (listing) {
        return listing;
      }
      return getListing(listingId);
    };

    fetchListing().then(listingData => {
      setListing(listingData);
      setMessage(curState => ({ ...curState, subject: listingData.line1 }));
      setDataLoaded(true);
    });
  }, [listing, listingId]);

  const fetchAgents = useCallback(
    async (
      listingId_: string,
      sentTo_: string,
      listing_: Listing,
      listingAgentsRemoved_: ListingAgentType[],
      agentsRemoved_: RecipientType[],
    ) => {
      setAgentsLoaded(false);
      getOfferAgents(listingId_, sentTo_, 'propertyAnnouncements')
        .then(result => {
          const {
            agentsSubscribed,
            agentsUnsubscribed,
            appointments,
          }: {
            agentsSubscribed: RecipientType[];
            agentsUnsubscribed: RecipientType[];
            appointments: EnhancedAppointmentVT[];
          } = result;

          const coopEmailToAppointmentsMap = appointments
            .sort((a, b) => {
              if (!b) return -1;
              if (!a) return 1;
              return moment(b.time).unix() - moment(a.time).unix();
            })
            .reduce(
              (
                acc: { [index: string]: EnhancedAppointmentVT[] },
                appointment,
              ) => {
                const coopEmail = appointment.coop && appointment.coop.email;

                if (!acc[coopEmail]) {
                  acc[coopEmail] = [appointment];
                } else {
                  acc[coopEmail].push(appointment);
                }

                return acc;
              },
              {},
            );

          // Map the recent appointment to each agent
          const agentsRecipientData = agentsSubscribed.map(
            (agent: RecipientType) => {
              const enhancedAgent = { ...agent };
              const agentAppointments = coopEmailToAppointmentsMap[agent.email];
              if (!agentAppointments) return enhancedAgent;

              const latestAppointment = agentAppointments[0];

              enhancedAgent.appointment = {
                id: latestAppointment.id,
                status: latestAppointment.status,
                displayStatus: latestAppointment.displayStatus,
                time: latestAppointment.time,
                timezone:
                  latestAppointment.timezone ??
                  latestAppointment.listing.timezone,
              };

              return enhancedAgent;
            },
          );

          const agentsUnsubscribedData = agentsUnsubscribed.map(
            (agent: RecipientType) => {
              const enhancedAgent = { ...agent };
              const agentAppointments = coopEmailToAppointmentsMap[agent.email];
              if (!agentAppointments) return enhancedAgent;

              const latestAppointment = agentAppointments[0];

              enhancedAgent.appointment = {
                id: latestAppointment.id,
                status: latestAppointment.status,
                displayStatus: latestAppointment.displayStatus,
                time: latestAppointment.time,
                timezone:
                  latestAppointment.timezone ??
                  latestAppointment.listing.timezone,
              };

              return enhancedAgent;
            },
          );
          setAgentsUnsubscribedState(agentsUnsubscribedData);
          const listingAgentsRecipientData: ListingAgentType[] =
            getListingAgents(listing_);
          const agentsRecipientDataWitoutDuplicates = checkForDuplicatesLA(
            agentsRecipientData,
            listingAgentsRecipientData,
          );
          return {
            listingAgentsRecipientData,
            agentsRecipientDataWitoutDuplicates,
          };
        })
        .then(
          ({
            listingAgentsRecipientData,
            agentsRecipientDataWitoutDuplicates,
          }) => {
            const {
              cleanedListingAgentsRecipient,
              cleanedAgentsRecipient,
              newAgentsRemoved,
              newListingAgentsRemoved,
            } = checkIfRecipientRemoved(
              agentsRecipientDataWitoutDuplicates,
              listingAgentsRecipientData,
              listingAgentsRemoved_,
              agentsRemoved_,
            );

            setListingAgentsRecipient(cleanedListingAgentsRecipient);
            setAgentsRecipient(cleanedAgentsRecipient);
            if (newAgentsRemoved.length) {
              setAgentsRemoved(newAgentsRemoved);
            }
            if (newListingAgentsRemoved.length) {
              setListingAgentsRemoved(newListingAgentsRemoved);
            }
          },
        )
        .finally(() => {
          setAgentsLoaded(true);
        });
    },
    [],
  );

  const hasFeature = (feature: OrgFeatures) =>
    Boolean($rootScope.auth.assumingUserId) ||
    Boolean($rootScope.auth.isAssumedNotBB) ||
    $rootScope.auth.org.features.includes(feature);

  useEffect(() => {
    if (listing && listing.reps) {
      fetchAgents(
        listingId,
        sentTo,
        listing,
        listingAgentsRemoved,
        agentsRemoved,
      );
    }
  }, [listing, listingId, sentTo, fetchAgents]);

  useEffect(() => {
    Promise.all([
      getTemplates(listingId, 'propertyAnnouncement'),
      getEmailSignatures(listingId),
    ])
      .then(([templates_, { signature: signatureData }]) => {
        const stateTemplates = templates_
          .map((foundTemplate: any) => ({
            ...foundTemplate,
            body: foundTemplate.body.concat(signatureData),
          }))
          .filter((_template: any) => {
            if (
              $rootScope.auth.roles[$rootScope.auth.role] >=
              $rootScope.auth.roles.BRANCH_ADMIN
            ) {
              return true; // branch admin or above should see all template regardless
            }

            const templateForOtherBranch =
              _template.role === 'BRANCH' &&
              !_template.location.includes($rootScope.auth.location);
            return !templateForOtherBranch; // should allow all global and agent scoped template
          });
        setTemplates(stateTemplates);
        setSignature(signatureData);
      })
      .then(() => {
        setTemplateLoaded(true);
      })
      .catch((err: any) => {
        $scope.$apply(() => {
          const { data } = err.response;
          $location.path(`/listing/${listingId}/view`);
          if (data.type === 'FORBIDDEN_ACTION') {
            data.message =
              'You do not have permissions to view this page. Please contact support@brokerbay.com for more information.';
          }
          ErrorService.toastyHTTPErr(err.response);
        });
        return errorReporter.error(err);
      });
  }, [
    listingId,
    $rootScope.auth.location,
    $rootScope.auth.role,
    $rootScope.auth.roles,
  ]);

  return (
    <>
      {!dataLoaded && templateLoaded && (
        <div className="normalheader transition animated fadeIn">
          <div className="hpanel">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-2 col-md-offset-5 spin">
                  <Spin />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {dataLoaded && templateLoaded && (
        <OfferMessageHeader
          listing={listing}
          listingId={listingId}
          onSend={onSend}
          $rootScope={$rootScope}
          ModalService={ModalService}
          sendButtonDisabled={
            hasLimitedAccess ||
            (agentsRecipient.length === 0 &&
              listingAgentsRecipient.length === 0) ||
            !(message.subject && message.body)
          }
          listingLink={listingLink}
        />
      )}
      {dataLoaded && templateLoaded && (
        <div className="content animate-panel">
          <div className="row">
            <div className="col-md-12">
              <div className="hpanel">
                <div className="panel-body" style={panelBodyStyles}>
                  {/* <!-- Tab Nav --> */}
                  <OfferMessageTabNav
                    setMainTab={setMainTab}
                    mainTab={mainTab}
                  />

                  {/* <!-- Contents --> */}
                  {mainTab === 'sendNewMessage' && (
                    <div className="row m-t-lg">
                      {/* <!-- Agent List --> */}
                      {!agentsLoaded && (
                        <div className="col-md-4">
                          <div className="col-md-2 col-md-offset-5 spin">
                            <Spin />
                          </div>
                        </div>
                      )}
                      {agentsLoaded && (
                        <AgentsList
                          agentsRecipient={agentsRecipient}
                          agentsRemoved={agentsRemoved}
                          agentsUnsubscribed={agentsUnsubscribedState}
                          listingAgentsRecipient={listingAgentsRecipient}
                          listingAgentsRemoved={listingAgentsRemoved}
                          listing={listing}
                          listingId={listingId}
                          deleteListingAgent={deleteListingAgent}
                          restoreListingAgent={restoreListingAgent}
                          deleteAgent={deleteAgent}
                          restoreAgent={restoreAgent}
                          fetchAgents={fetchAgents}
                          sentTo={sentTo}
                          agentsListTab={agentsListTab}
                          setAgentsListTab={setAgentsListTab}
                        />
                      )}

                      {/* <!-- Messages --> */}
                      <div className="col-md-8">
                        <div className="row mg-b-lg">
                          <div className="col-md-12">
                            <div className="form-inline">
                              <div className="form-group">
                                <label>
                                  <h4>Send Message To:</h4>
                                </label>
                                <select
                                  className="form-control"
                                  value={sentTo}
                                  onChange={e => {
                                    setSentTo(e.target.value);
                                  }}
                                  data-cy="select-send-message-to"
                                >
                                  {messageSentToOptions(hasFeature).map(
                                    option => (
                                      <option
                                        value={option.value}
                                        key={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ),
                                  )}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12">
                            <OfferMessageEditor
                              templates={templates}
                              message={message}
                              signature={signature}
                              defaultSubjectPostfix={listing.line1}
                              setSubject={setSubject}
                              setBody={setBody}
                              hasFeature={hasFeature}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {mainTab === 'sentMessage' && (
                    <OfferMessageSentPage listingId={listingId} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default OfferMessage;
